import {
  FloorTagged,
  SegmentHit,
  SpatialQueryGrid,
  VolumeHeightSample,
  VolumeLeafCallback,
} from '../SpatialQueryGrid';
import { BvhSpatialGrid, TerrainFallback } from './BvhSpatialGrid';

/**
 * Per-tile BVH builder injection point. Called by `TiledBvh.loadTile` for
 * each tile that enters the camera ring. The implementation is responsible
 * for:
 *   - fetching the tile archive (terrain + object placements)
 *   - resolving each placement's mesh template into a BLAS (via `BlasStore`)
 *   - building the TLAS and wrapping it in a `BvhSpatialGrid`
 *
 * Returning `null` means "no BVH coverage for this tile" — heightmap
 * fallback will serve any query against this tile, same as today's volgrid
 * path for tiles with no published volgrid.
 *
 * Kept as an injection point rather than wiring tile-archive + object-streamer
 * + model-registry internals directly into this class, so the streamer stays
 * decoupled from those subsystems' concrete types.
 */
export type TiledBvhGenerator = (tx: number, tz: number) => Promise<BvhSpatialGrid | null>;

/**
 * Camera-driven tiled BVH. Drop-in replacement for `TiledBvh` —
 * implements `SpatialQueryGrid`, owns a 3×3 ring of per-tile `BvhSpatialGrid`s,
 * applies terrain fallback at the tile-level for queries that miss the BVH.
 *
 * Mirrors `TiledBvh` lifecycle exactly:
 *   - `updateCamera(x, z)` each frame to refresh the ring
 *   - `loadInitialTiles(x, z)` blocks until the inner 3×3 is loaded
 *   - `setTerrainFallback(fn)` registers the smooth heightmap fallback
 *   - tiles evict on camera tile change
 */
export class TiledBvh implements SpatialQueryGrid {
  public voxelSize: number;

  private tilesX: number;
  private tilesZ: number;
  private tileWorldSize: number;
  private worldMinX: number;
  private worldMinZ: number;

  private loaded = new Map<string, BvhSpatialGrid>();
  private loading = new Set<string>();
  private lastCameraTileX = -999;
  private lastCameraTileZ = -999;

  private terrainFallback: TerrainFallback | null = null;
  private generator: TiledBvhGenerator;

  /**
   * Max in-flight tile loads. Capped to keep the worker queue focused on the
   * most recent ring during fast flight (matches `TiledBvh` rationale).
   */
  private static readonly MAX_INFLIGHT_LOADS = 3;

  constructor(
    tilesX: number,
    tilesZ: number,
    tileWorldSize: number,
    worldMinX: number,
    worldMinZ: number,
    voxelSize: number,
    generator: TiledBvhGenerator,
  ) {
    this.tilesX = tilesX;
    this.tilesZ = tilesZ;
    this.tileWorldSize = tileWorldSize;
    this.worldMinX = worldMinX;
    this.worldMinZ = worldMinZ;
    this.voxelSize = voxelSize;
    this.generator = generator;
  }

  setTerrainFallback(fn: TerrainFallback | null): void {
    this.terrainFallback = fn;
  }

  hasTileAt(x: number, z: number): boolean {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    return this.loaded.has(`${tx},${tz}`);
  }

  /** Refresh the ring each frame. Mirrors `TiledBvh.updateCamera`. */
  updateCamera(x: number, z: number): void {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    const tileChanged = tx !== this.lastCameraTileX || tz !== this.lastCameraTileZ;
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    const needed = new Set<string>();
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          needed.add(`${ntx},${ntz}`);
        }
      }
    }

    if (tileChanged) {
      for (const key of this.loaded.keys()) {
        if (!needed.has(key)) this.loaded.delete(key);
      }
    }

    for (const key of needed) {
      if (this.loading.size >= TiledBvh.MAX_INFLIGHT_LOADS) break;
      if (this.loaded.has(key) || this.loading.has(key)) continue;
      this.loadTile(key);
    }
  }

  async loadTileAt(tx: number, tz: number): Promise<void> {
    const key = `${tx},${tz}`;
    if (this.loaded.has(key) || this.loading.has(key)) return;
    await this.loadTile(key);
  }

  /** Load the initial 3×3 ring and wait for it. Mirrors `TiledBvh.loadInitialTiles`. */
  async loadInitialTiles(x: number, z: number): Promise<void> {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;
    const promises: Promise<void>[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          promises.push(this.loadTileAt(ntx, ntz));
        }
      }
    }
    await Promise.all(promises);
  }

  private async loadTile(key: string): Promise<void> {
    this.loading.add(key);
    const [txStr, tzStr] = key.split(',');
    const tx = parseInt(txStr);
    const tz = parseInt(tzStr);
    try {
      const bvh = await this.generator(tx, tz);
      if (!bvh) return;
      // Drop stale results (camera flew past).
      const dx = Math.abs(tx - this.lastCameraTileX);
      const dz = Math.abs(tz - this.lastCameraTileZ);
      if (dx > 1 || dz > 1) return;
      this.loaded.set(key, bvh);
    } catch (e) {
      console.warn(`Failed to build BVH for tile (${tx},${tz}):`, e);
    } finally {
      this.loading.delete(key);
    }
  }

  private getTile(x: number, z: number): BvhSpatialGrid | undefined {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    return this.loaded.get(`${tx},${tz}`);
  }

  // --- SpatialQueryGrid: delegate to per-tile BVH, apply fallback at tile level ---

  /**
   * Apply terrain fallback. Same shape as `TiledBvh.resolveFloorY`:
   * substitute the smooth heightmap Y when the tile result is missing OR
   * when it's tagged as terrain-origin.
   */
  private resolveFloorY(tile: FloorTagged | null | undefined, x: number, z: number): number | null {
    if (tile == null) return this.terrainFallback?.(x, z)?.height ?? null;
    if (!tile.terrainOrigin) return tile.y;
    const fb = this.terrainFallback?.(x, z);
    return fb?.height ?? tile.y;
  }

  getFloorHeight(x: number, y: number, z: number): number | null {
    const tile = this.getTile(x, z)?.getFloorHeightTagged(x, y, z);
    return this.resolveFloorY(tile, x, z);
  }

  getFloorHeightTagged(x: number, y: number, z: number): FloorTagged | null {
    return this.getTile(x, z)?.getFloorHeightTagged(x, y, z) ?? null;
  }

  getCeilingHeight(x: number, y: number, z: number): number | null {
    return this.getTile(x, z)?.getCeilingHeight(x, y, z) ?? null;
  }

  getCeilingHeightAbove(x: number, y: number, z: number): number | null {
    return this.getTile(x, z)?.getCeilingHeightAbove(x, y, z) ?? null;
  }

  getFloorHeightBelow(x: number, y: number, z: number): number | null {
    const tile = this.getTile(x, z)?.getFloorHeightBelowTagged(x, y, z);
    return this.resolveFloorY(tile, x, z);
  }

  getFloorHeightBelowTagged(x: number, y: number, z: number): FloorTagged | null {
    return this.getTile(x, z)?.getFloorHeightBelowTagged(x, y, z) ?? null;
  }

  getFloorHeightBelowSmooth(x: number, y: number, z: number): number | null {
    const tile = this.getTile(x, z);
    if (!tile) return this.terrainFallback?.(x, z)?.height ?? null;
    // BVH-side smoothing already does 4-sample bilinear; check whether any
    // neighbour would have been served by the heightmap fallback (no tile or
    // terrain-origin) — if so, defer to the fallback for a continuous result
    // across mixed/terrain seams. Mirrors TiledBvh behavior.
    if (this.shouldUseFallbackForSmoothFloor(tile, x, y, z)) {
      const fb = this.terrainFallback?.(x, z);
      if (fb !== null && fb !== undefined) return fb.height;
    }
    return tile.getFloorHeightBelowSmooth(x, y, z);
  }

  private shouldUseFallbackForSmoothFloor(tile: BvhSpatialGrid, x: number, y: number, z: number): boolean {
    if (this.terrainFallback === null) return false;
    const vs = this.voxelSize;
    const samples: Array<[number, number]> = [
      [x, z],
      [x + vs, z],
      [x, z + vs],
      [x + vs, z + vs],
    ];
    for (const [sx, sz] of samples) {
      const r = tile.getFloorHeightBelowTagged(sx, y, sz);
      if (r === null || r.terrainOrigin) return true;
    }
    return false;
  }

  getFloorHeightWithNormal(x: number, y: number, z: number): VolumeHeightSample | null {
    const tile = this.getTile(x, z);
    const tagged = tile?.getFloorHeightTagged(x, y, z);
    if (tagged && !tagged.terrainOrigin) {
      return tile!.getFloorHeightWithNormal(x, y, z);
    }
    const fb = this.terrainFallback?.(x, z);
    if (fb !== null && fb !== undefined) return fb;
    if (tagged) return tile!.getFloorHeightWithNormal(x, y, z);
    return null;
  }

  forEachLeaf(callback: VolumeLeafCallback): void {
    for (const tile of this.loaded.values()) tile.forEachLeaf(callback);
  }

  // Segment-cast methods iterate every loaded tile rather than special-casing
  // the single-tile path. Each per-tile `raycastTlas` does an O(1) root-AABB
  // rejection up front, so tiles the segment doesn't enter cost ~one ray-AABB
  // test. With ≤9 tiles in the ring this is negligible and avoids a latent
  // correctness gap of the old fast path: a segment whose endpoints both sat
  // inside tile A could still need to hit geometry placed in tile B whose
  // BLAS extends across the boundary (buildings on a tile edge, etc).

  segmentHitDistance(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): number | null {
    let bestT: number | null = null;
    for (const tile of this.loaded.values()) {
      const t = tile.segmentHitDistance(x0, y0, z0, x1, y1, z1);
      if (t !== null && (bestT === null || t < bestT)) bestT = t;
    }
    return bestT;
  }

  segmentHitWithNormal(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): SegmentHit | null {
    let best: SegmentHit | null = null;
    for (const tile of this.loaded.values()) {
      const hit = tile.segmentHitWithNormal(x0, y0, z0, x1, y1, z1);
      if (hit !== null && (best === null || hit.t < best.t)) best = hit;
    }
    return best;
  }

  rayCrossesSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
    for (const tile of this.loaded.values()) {
      if (tile.rayCrossesSolid(x0, y0, z0, x1, y1, z1)) return true;
    }
    return false;
  }
}
